import baseColors from './colors';
import darkOverrides from './darkColorOverrides';
import { THEME_MODES } from './constants';

/**
 * Cheatsheet de tokens semanticos (PT-BR)
 *
 * Regras:
 * - Prefira tokens semanticos em componentes visuais.
 * - Evite usar cores primitivas direto, exceto casos especificos.
 * - Nao use theme.isDark em estilos de componente.
 *
 * Como escolher:
 * - Fundo -> surface.*
 * - Texto -> text.*
 * - Borda -> border.*
 * - Acao principal -> action.primary
 * - Feedback de estado -> status.*
 * - Busca (SearchBar) -> search.*
 *
 * Tokens mais usados:
 * - surface.card: fundo de card/modal
 * - surface.muted: fundo sutil/disabled
 * - text.primary/secondary/inverse
 * - border.default/subtle
 * - status.success/warning/info/danger/attention
 * - status.maintenanceBanner/maintenanceStripe/maintenanceTag/maintenanceTagText (faixa de place em manutencao)
 * - status.success/warning/info/danger/attention/owner
 * - status.maintenance/commercial/operational/specialized (dominios de badge de tipo de OS)
 * - overlay.default (backdrop/overlay semitransparente)
 * - table.header/hover/background/cellBorder (tabelas)
 * - shadows: use theme.colors.semantic.shadow.sm | .md | .lg (box-shadow por nivel, light/dark)
 *
 * Exemplos em styled-components:
 * - background: ${({ theme }) => theme.colors.semantic.surface.card};
 * - color: ${({ theme }) => theme.colors.semantic.text.primary};
 * - border: 1px solid ${({ theme }) => theme.colors.semantic.border.default};
 */

const semanticTokens = {
  [THEME_MODES.light]: {
    surface: {
      page: baseColors.lightnessGreenSurface,
      card: baseColors.white,
      muted: baseColors.hoverGrey,
      selection: baseColors.primary50,
      delete: baseColors.danger500,
      infoEmphasis: baseColors.info100,
      badgeWarning: baseColors.warning500,
      badgeNeutral: baseColors.secondary800,
      badgeAttention: baseColors.orange500,
      navbar: baseColors.strongGreenSurface,
      navbarActive: baseColors.greenSurface,
    },
    text: {
      primary: baseColors.highBlack,
      secondary: baseColors.mediumBlack,
      placeholder: baseColors.secondary500,
      inverse: baseColors.highWhite,
    },
    border: {
      default: baseColors.secondary200,
      subtle: baseColors.lightGreyBorder,
    },
    action: {
      primary: baseColors.primary500,
    },
    status: {
      success: baseColors.success50,
      warning: baseColors.warning50,
      info: baseColors.info50,
      danger: baseColors.danger50,
      // tom mais claro/dessaturado de fundo critico (ex.: card/header de
      // accordion no foco do evento), mais sutil que `danger` (usado em banners)
      dangerSubtle: baseColors.danger20,
      attention: baseColors.orange50,
      owner: baseColors.violet50,
      maintenanceBanner: baseColors.warning700,
      maintenanceStripe: baseColors.gray900,
      maintenanceTag: baseColors.warning500,
      maintenanceTagText: baseColors.highBlack,
      maintenance: baseColors.maintenance500,
      commercial: baseColors.commercial500,
      operational: baseColors.operational500,
      specialized: baseColors.specialized500,
    },
    search: {
      background: baseColors.blue100,
      border: baseColors.blue400,
    },
    media: {
      frameBackground: baseColors.gray900,
      actionAreaBackground: baseColors.highBlack,
      overlayBackground: 'rgba(28, 28, 29, 0.85)',
      controlPanelBackground: 'rgba(28, 28, 29, 0.6)',
      controlColor: baseColors.highWhite,
    },
    table: {
      header: baseColors.tableHeader,
      hover: baseColors.tableHover,
      background: baseColors.tableBg,
      cellBorder: baseColors.tableCellBorder,
    },
    shadow: {
      sm: '0 2px 8px rgba(0, 0, 0, 0.12)',
      md: '0 4px 12px rgba(0, 0, 0, 0.38)',
      lg: '0 8px 20px rgba(0, 0, 0, 0.2)',
    },
    badge: {
      primaryToken: 'secondary50',
      primaryBorderToken: 'secondary300',
      doorToken: 'secondary300',
    },
    overlay: {
      default: 'rgba(0, 0, 0, 0.5)',
      hover: 'rgba(0, 0, 0, 0.3)',
    },
  },
  [THEME_MODES.dark]: {
    surface: {
      page: darkOverrides.pageSurface,
      card: darkOverrides.whiteSurface,
      muted: darkOverrides.mutedSurface,
      selection: darkOverrides.selectionSurface,
      delete: darkOverrides.deleteSurface,
      infoEmphasis: darkOverrides.mutedSurface,
      badgeWarning: darkOverrides.mutedSurface,
      badgeNeutral: darkOverrides.mutedSurface,
      badgeAttention: darkOverrides.mutedSurface,
      navbar: darkOverrides.navbarSurface,
      navbarActive: darkOverrides.navbarActiveSurface,
    },
    text: {
      primary: darkOverrides.highBlack,
      secondary: 'rgba(255, 255, 255, 0.7)',
      placeholder: 'rgba(255, 255, 255, 0.5)',
      inverse: darkOverrides.highBlack,
    },
    border: {
      default: darkOverrides.secondary200,
      subtle: darkOverrides.lightGreyBorder,
    },
    action: {
      primary: baseColors.primary500,
    },
    status: {
      success: darkOverrides.successSurface,
      warning: darkOverrides.warningSurface,
      info: darkOverrides.infoSurface,
      danger: darkOverrides.dangerSurface,
      // espelha `dangerSubtle` do light (mais recuado que `danger`, que ja
      // e o tom base no dark)
      dangerSubtle: darkOverrides.dangerSubtleSurface,
      attention: darkOverrides.attentionSurface,
      owner: darkOverrides.ownerSurface,
      maintenanceBanner: baseColors.warning500,
      maintenanceStripe: baseColors.highBlack,
      maintenanceTag: baseColors.warning150,
      maintenanceTagText: baseColors.highBlack,
      maintenance: darkOverrides.maintenanceSurface,
      commercial: darkOverrides.commercialSurface,
      operational: darkOverrides.operationalSurface,
      specialized: darkOverrides.specializedSurface,
    },
    search: {
      background: darkOverrides.mutedSurface,
      border: baseColors.blue400,
    },
    media: {
      frameBackground: baseColors.highBlack,
      actionAreaBackground: darkOverrides.mutedSurface,
      overlayBackground: 'rgba(0, 0, 0, 0.85)',
      controlPanelBackground: 'rgba(0, 0, 0, 0.6)',
      controlColor: baseColors.highWhite,
    },
    table: {
      header: darkOverrides.tableHeader,
      hover: darkOverrides.tableHover,
      background: darkOverrides.tableBg,
      cellBorder: darkOverrides.tableCellBorder,
    },
    shadow: {
      sm: '0 2px 8px rgba(0, 0, 0, 0.24)',
      md: '0 4px 12px rgba(0, 0, 0, 0.35)',
      lg: '0 8px 24px rgba(0, 0, 0, 0.4)',
    },
    badge: {
      primaryToken: 'secondary700',
      primaryBorderToken: 'secondary500',
      doorToken: 'secondary700',
    },
    overlay: {
      default: 'rgba(0, 0, 0, 0.7)',
      hover: 'rgba(0, 0, 0, 0.5)',
    },
  },
};

export default semanticTokens;
